// @ts-nocheck
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { LuTrash2 } from 'react-icons/lu';
import { IoMdAddCircle } from 'react-icons/io';
import { AppButton } from '@/components/atoms/AppButton';
import { useDebounce } from '@/hooks/useDebounce';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { DataTable } from '@/components/organism/Table';
import FullPageCard from '@/components/organism/Cards/FullPageCard';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import GlobalSearch from '@/components/atoms/GlobalSearch';
import Autocomplete from '@/components/elements/AutoComplete';
import CommonDropdownSelector from '@/components/organism/GroupDropdown';
import SegmentedControl from '@/components/elements/SegmentedControl';
import ConfirmActionModal, { DeleteIcon, ActivateIcon } from '@/components/organism/ConfirmActionModal';
import { PROVIDER_CONSTANT_PROPS } from '@/constant/provider/provider';
import { OPERATOR_PROPS } from '@/constant/operator/operator.constant';
import { getOperatorDetail } from '@/store/selectors/operator.selector';
import { subApplicationList } from '@/store/selectors/subApplication.selector';
import {
  getOperators,
  setShouldRefetchOperators,
  deleteOperator,
  activateOperator,
  clearOperatorError,
} from '@/store/slices/operator.slice';
import { getSubApplications } from '@/store/slices/subApplication.slice';
import {
  ROW_PER_PAGE_OPTION,
  pagination as PAGINATION_INITIAL_STATE,
  authorizeRole,
  STRINGS,
  ALL_PROVIDER,
  ALL_SUB_APPLICATION,
  SortEnum,
} from '@/constant/global/global.constant';
import { getRole, ROLES } from '@/constant/users/user.constant';
import type { Operator } from '@/store/types';
import { NAV_ITEMS, getNavItemIcon } from '@/constant/layout/layout.constant';
import { OPERATOR_TEST_IDS as TID } from '@/constant/testIds/operator.testIds';
import { tid } from '@/utils/testId';
import { buildColumns } from './operator.columns';
import OperatorModal from './CrudOperatorModal';

/* ── Component ────────────────────────────────────────────────────────────── */
const OperatorComponent = () => {
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [editOperatorData, setEditOperatorData] = useState<Operator | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<Operator | null>(null);
  const [activateTarget, setActivateTarget] = useState<Operator | null>(null);
  const [isCopy, setIsCopy] = useState('');
  const isCopyRef = useRef('');
  const [pagination, setPagination] = useState(PAGINATION_INITIAL_STATE);
  const [selectedProvider, setSelectedProvider] = useState(ALL_PROVIDER);
  const [sorting, setSorting] = useState({
    orderBy: 'updatedOn',
    order: 'desc' as SortEnum,
  });
  const [search, setSearch] = useState('');
  const [searchApplication, setSearchApplication] = useState('');
  const [selectedSubApplication, setSelectedSubApplication] =
    useState(ALL_SUB_APPLICATION);
  const [activeStatus, setActiveStatus] = useState<string>('1');

  const operatorNameSearchQuery = useDebounce(
    search,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const subApplicationListData = useSelector(subApplicationList);
  const userHasWriteAccess = authorizeRole.PORTAL.CREATE.includes(getRole());
  const isAdmin = getRole() === ROLES.ADMIN;

  const {
    loading,
    error,
    operators: { data, pagination: paginationResponse },
    shouldRefetchOperators,
    message
  } = useSelector(getOperatorDetail);

  const totalPages = Math.ceil(
    (paginationResponse?.totalRecords || 0) / (pagination.limit || 10),
  );

  // hasLoaded ref — flips once on first data arrival, never flips back
  const hasLoaded = useRef(false);

  const onCopyClick = useCallback((key: string) => {
    isCopyRef.current = key;
    setIsCopy(key);
    setTimeout(() => {
      isCopyRef.current = '';
      setIsCopy('');
    }, 500);
  }, []);

  const handlePageSizeChange = useCallback(
    (value: number) => {
      setPagination((p) => ({
        ...p,
        limit: value,
        page: totalPages < p.page ? totalPages : p.page,
      }));
    },
    [totalPages],
  );

  const handlePreviousClick = useCallback((page: number) => {
    if (page > 1) setPagination((p) => ({ ...p, page: page - 1 }));
  }, []);

  const handleNextClick = useCallback(
    (page: number) => {
      if (page < totalPages) setPagination((p) => ({ ...p, page: page + 1 }));
    },
    [totalPages],
  );

  const handleSelectedProvider = useCallback((value: any) => {
    setSelectedProvider(value?.provider ?? ALL_PROVIDER);
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const handleSubApplicationId = useCallback((value: any) => {
    setSelectedSubApplication(value ?? ALL_SUB_APPLICATION);
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  // Track first successful data load
  useEffect(() => {
    if (!hasLoaded.current && Array.isArray(data) && data.length > 0) {
      hasLoaded.current = true;
    }
  }, [data]);

  // Reset to page 1 if current page overshoots
  useEffect(() => {
    if (pagination?.page > totalPages && totalPages !== 0) {
      setPagination((p) => ({ ...p, page: 1 }));
    }
  }, [totalPages]);

  // Main fetch effect
  useEffect(() => {
    if (
      operatorNameSearchQuery?.length > 0 &&
      operatorNameSearchQuery?.length < 3
    )
      return;

    dispatch(
      getOperators({
        ...sorting,
        ...pagination,
        ...(operatorNameSearchQuery?.trim()?.length >= 3
          ? { name: operatorNameSearchQuery.trim() }
          : {}),
        providerId: selectedProvider?.id,
        subapplicationId: selectedSubApplication?.id,
        includeInactive: activeStatus === 'all' || activeStatus === '0' ? true : undefined,
        inactive: activeStatus === '0' ? true : undefined,
      }),
    );
  }, [
    operatorNameSearchQuery,
    selectedSubApplication?.id,
    selectedProvider?.id,
    pagination?.page,
    pagination?.limit,
    sorting?.orderBy,
    sorting?.order,
    activeStatus,
  ]);

  // Re-fetch after create / update / delete / activate
  useEffect(() => {
    if (shouldRefetchOperators) {
      setDeleteTarget(null);
      setActivateTarget(null);
      dispatch(
        getOperators({
          ...sorting,
          ...pagination,
          providerId: selectedProvider?.id,
          subapplicationId: selectedSubApplication?.id,
          includeInactive: activeStatus === 'all' || activeStatus === '0' ? true : undefined,
          inactive: activeStatus === '0' ? true : undefined,
        }),
      );
      dispatch(setShouldRefetchOperators(false));
    }
  }, [shouldRefetchOperators]);


  // Load subapplications once on mount
  useEffect(() => {
    dispatch(getSubApplications({ limit: 100, page: 1 }));
  }, []);

  const onEdit = useCallback((item: Operator) => setEditOperatorData(item), []);
  const onDelete = useCallback((item: Operator) => setDeleteTarget(item), []);
  const onActivate = useCallback((item: Operator) => setActivateTarget(item), []);

  const columns = useMemo(
    () =>
      buildColumns(
        isCopyRef,
        onCopyClick,
        onEdit,
        onDelete,
        userHasWriteAccess,
        isAdmin,
        onActivate,
      ),
    [onCopyClick, onEdit, onDelete, userHasWriteAccess, isAdmin, onActivate],
  );

  return (
    <>
      <FullPageCard
        testId={tid(TID.PAGE)}
        className="rounded-b-none"
        header={
          <PageToolbar
            title={STRINGS.OPERATOR}
            subtitle={STRINGS.OPERATOR_SUB}
            icon={getNavItemIcon(NAV_ITEMS.OPERATORS)}
            actionsSlot={
              <GlobalSearch
                className="w-[220px]"
                placeholder={OPERATOR_PROPS.SEARCH_PLACE_HOLDER.PLACE_HOLDER}
                dataTestId={tid(TID.SEARCH_INPUT)}
                globalSearch={search}
                setGlobalSearch={(v) => {
                  setSearch(v);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
              />
            }
            actionButtons={
              userHasWriteAccess ? (
                <AppButton
                  data-testid={tid(TID.ADD_BTN)}
                  variant="primary"
                  icon={<IoMdAddCircle className="h-4 w-4" />}
                  onClick={() => setIsOpen(true)}
                >
                  {OPERATOR_PROPS.BUTTON.ADD}
                </AppButton>
              ) : null
            }
            filtersLeft={
              <div className="flex items-center gap-2">
                <CommonDropdownSelector
                  onSelectionChange={handleSelectedProvider}
                  showProvider
                  showOperator={false}
                  showLocation={false}
                  data-testid={tid('operator-provider-dropdown')}
                  dataTestId={tid(TID.FILTER_DROPDOWN)}
                />
                <div className="w-[190px]">
                  <Autocomplete
                    dataTestId={tid(`operator-${OPERATOR_PROPS.SUb_APPLICATION_ID.LABEL}`)}
                    label={OPERATOR_PROPS.SUb_APPLICATION_ID.LABEL}
                    placeholder={OPERATOR_PROPS.SUb_APPLICATION_ID.PLACEHOLDER}
                    handleSelectedValue={handleSubApplicationId}
                    selectedValue={selectedSubApplication}
                    lists={subApplicationListData}
                    nameKey="name"
                    ellipsisLength={22}
                  />
                </div>
              </div>
            }
            filtersRight={
              <SegmentedControl
                testId={tid(TID.STATUS_FILTER)}
                size="lg"
                shape="square"
                variant="gradient"
                options={[
                  { value: 'all', label: 'All' },
                  { value: '1', label: 'Active' },
                  { value: '0', label: 'Inactive' },
                ]}
                value={activeStatus}
                onChange={(v: string) => {
                  setActiveStatus(v);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
              />
            }
          />
        }
      >
        <DataTable
          data-testid={tid(TID.TABLE)}
          data={data ?? []}
          columns={columns}
          loading={loading}
          skeletonRows={15}
          onSort={(orderBy, order) =>
            setSorting({ orderBy, order: order as SortEnum })
          }
          sortState={sorting}
          rowKey={(item: any) => item?.operatorId ?? ''}
          rowTestId={(item: any, idx: number) =>
            TID.TABLE_ROW(`${idx}`)
          }
          pagination={
            !isEmpty(data)
              ? {
                state: pagination,
                totalItem: paginationResponse?.totalRecords,
                rowsPerPageOptions: ROW_PER_PAGE_OPTION,
                onRowsPerPageChange: handlePageSizeChange,
                onNextClick: handleNextClick,
                onPreviousClick: handlePreviousClick,
              }
              : null
          }
        />
      </FullPageCard>

      {/* Create modal */}
      <OperatorModal
        key={isOpen ? 'open' : 'closed'}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />

      {/* Edit modal */}
      <OperatorModal
        key={
          editOperatorData
            ? `edit-${editOperatorData.operatorId}`
            : 'edit-closed'
        }
        isOpen={!!editOperatorData}
        onClose={() => setEditOperatorData(null)}
        operatorData={editOperatorData}
      />

      {/* Delete confirmation modal */}
      <ConfirmActionModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget?.operatorId) {
            dispatch(deleteOperator(deleteTarget.operatorId as any));
          }
        }}
        title={OPERATOR_PROPS.DELETE_MODAL.TITLE}
        subtitle={OPERATOR_PROPS.DELETE_MODAL.SUBTITLE}
        description={
          <>
            {OPERATOR_PROPS.DELETE_MODAL.BODY_PREFIX}{' '}
            <span className="font-semibold text-gray-900 dark:text-white">
              {deleteTarget?.name}
            </span>
            ?
          </>
        }
        confirmWord={OPERATOR_PROPS.DELETE_MODAL.CONFIRM_WORD}
        placeholder={OPERATOR_PROPS.DELETE_MODAL.PLACEHOLDER}
        inputLabel={OPERATOR_PROPS.DELETE_MODAL.INPUT_LABEL}
        hint={OPERATOR_PROPS.DELETE_MODAL.HINT}
        confirmLabel={OPERATOR_PROPS.DELETE_MODAL.CONFIRM}
        confirmIcon={<DeleteIcon />}
        variant="danger"
        size="xl"
        icon={<DeleteIcon />}
        loading={loading}
      />

      {/* Reactivate confirmation modal */}
      <ConfirmActionModal
        isOpen={!!activateTarget}
        onClose={() => setActivateTarget(null)}
        onConfirm={() => {
          if (activateTarget?.operatorId) {
            dispatch(activateOperator(activateTarget as any));
          }
        }}
        title={OPERATOR_PROPS.ACTIVATE_MODAL.TITLE}
        subtitle={OPERATOR_PROPS.ACTIVATE_MODAL.SUBTITLE}
        description={
          <>
            {OPERATOR_PROPS.ACTIVATE_MODAL.BODY_PREFIX}{' '}
            <span className="font-semibold text-gray-900 dark:text-white">
              {activateTarget?.name}
            </span>
            ?
          </>
        }
        confirmWord={OPERATOR_PROPS.ACTIVATE_MODAL.CONFIRM_WORD}
        placeholder={OPERATOR_PROPS.ACTIVATE_MODAL.PLACEHOLDER}
        inputLabel={OPERATOR_PROPS.ACTIVATE_MODAL.INPUT_LABEL}
        hint={OPERATOR_PROPS.ACTIVATE_MODAL.HINT}
        confirmLabel={OPERATOR_PROPS.ACTIVATE_MODAL.CONFIRM_LABEL}
        confirmIcon={<ActivateIcon />}
        variant="success"
        size="xl"
        icon={<ActivateIcon />}
        loading={loading}
      />
    </>
  );
};

export default OperatorComponent;